import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Sparkles, Briefcase, MapPin, Building2, Trophy, Quote, Package,
  Linkedin, Instagram, Facebook, Twitter, Globe, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";

export default function PublicProfile() {
  const { id } = useParams();
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get(`/public/members/${id}`).then((r) => setM(r.data)).catch(() => setM(false));
  }, [id]);

  if (m === null) return <div className="min-h-screen flex items-center justify-center bg-ivory text-plumSoft">Loading…</div>;
  if (m === false) return (
    <div className="min-h-screen flex items-center justify-center bg-ivory text-center px-6">
      <div>
        <p className="overline">Not found</p>
        <h1 className="font-display text-3xl text-aubergine mt-3">This profile is no longer available</h1>
        <Link to="/" className="btn-primary mt-6 inline-flex">Go home</Link>
      </div>
    </div>
  );

  const socials = [
    m.linkedin && { Icon: Linkedin, href: m.linkedin.startsWith("http") ? m.linkedin : `https://linkedin.com/in/${m.linkedin}`, label: "LinkedIn" },
    m.instagram && { Icon: Instagram, href: m.instagram.startsWith("http") ? m.instagram : `https://instagram.com/${m.instagram}`, label: "Instagram" },
    m.facebook && { Icon: Facebook, href: m.facebook.startsWith("http") ? m.facebook : `https://facebook.com/${m.facebook}`, label: "Facebook" },
    m.twitter && { Icon: Twitter, href: m.twitter.startsWith("http") ? m.twitter : `https://x.com/${m.twitter}`, label: "Twitter / X" },
    m.website && { Icon: Globe, href: m.website, label: "Website" },
  ].filter(Boolean);

  return (
    <div data-testid="public-profile" className="min-h-screen bg-ivory">
      {/* HERO BAND */}
      <section className="relative bg-gradient-to-br from-aubergine via-plum to-rose text-ivory overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="container-luxe relative pt-12 pb-40 md:pt-16 md:pb-56">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="inline-flex items-center gap-2">
              <img src="/wowshe-logo.png" alt="WOWSHE" className="h-16 md:h-24 w-auto object-contain drop-shadow-lg" />
            </Link>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-champagne text-aubergine text-[10px] md:text-xs uppercase tracking-widest font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Proud WOWSHE Member
            </span>
          </div>
        </div>
      </section>

      {/* CARD */}
      <div className="container-luxe -mt-24 md:-mt-28 relative pb-16">
        <article className="bg-white rounded-3xl border border-aubergine/8 shadow-luxe relative">
          <div className="p-6 md:p-10">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
              <div className="w-40 h-40 md:w-52 md:h-52 rounded-2xl overflow-hidden bg-lavender border-4 border-white shadow-luxe shrink-0 -mt-28 md:-mt-36">
                {m.avatar
                  ? <img src={m.avatar} alt={m.name} className="w-full h-full object-cover object-center" />
                  : <div className="w-full h-full flex items-center justify-center text-plum font-display text-7xl">{m.name?.[0]}</div>
                }
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-3xl md:text-5xl text-aubergine leading-tight">{m.name}</h1>
                <p className="text-plumSoft mt-2 text-lg">{m.business || "WOWSHE community member"}</p>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-plum">
                  {m.industry && <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />{m.industry}</span>}
                  {m.city && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{m.city}</span>}
                  {m.chapter && <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{m.chapter} Chapter</span>}
                </div>

                {socials.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {socials.map((s) => (
                      <a key={s.label} href={s.href} target="_blank" rel="noreferrer" data-testid={`pub-social-${s.label.toLowerCase().split(" ")[0]}`} className="w-10 h-10 rounded-full border border-aubergine/15 text-aubergine flex items-center justify-center hover:bg-aubergine hover:text-ivory transition-all" title={s.label}>
                        <s.Icon className="w-4 h-4" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BIO */}
          {m.bio && (
            <div className="px-6 md:px-10 pb-2">
              <p className="overline">About</p>
              <p className="mt-3 text-plumSoft font-light leading-relaxed whitespace-pre-line max-w-3xl">{m.bio}</p>
            </div>
          )}

          {/* Achievements */}
          {m.achievements?.length > 0 && (
            <div className="px-6 md:px-10 pt-8">
              <div className="flex items-center gap-2 mb-5"><Trophy className="w-5 h-5 text-rose" /><p className="overline !text-rose">Achievements</p></div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {m.achievements.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-lavender/40">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose to-champagne text-ivory flex items-center justify-center font-display text-sm shrink-0">{i + 1}</div>
                    <p className="text-sm text-aubergine leading-relaxed pt-1">{a}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Testimonials */}
          {m.testimonials?.length > 0 && (
            <div className="px-6 md:px-10 pt-8">
              <div className="flex items-center gap-2 mb-5"><Quote className="w-5 h-5 text-plum" /><p className="overline !text-plum">Testimonials</p></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {m.testimonials.map((t, i) => (
                  <article key={i} className="p-5 rounded-xl bg-gradient-to-br from-lavender/60 to-white border border-aubergine/8">
                    <Quote className="w-5 h-5 text-rose/60 mb-3" />
                    <p className="text-sm text-aubergine font-light italic leading-relaxed">"{t.quote}"</p>
                    <div className="mt-4 flex items-center gap-3">
                      {t.avatar ? (
                        <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-plum/15 text-plum flex items-center justify-center text-sm font-display">{t.author?.[0]}</div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm text-aubergine font-medium truncate">{t.author}</p>
                        {t.role && <p className="text-xs text-plumSoft truncate">{t.role}</p>}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* Catalogue */}
          {m.products?.length > 0 && (
            <div className="px-6 md:px-10 pt-8 pb-2">
              <div className="flex items-center gap-2 mb-5"><Package className="w-5 h-5 text-teal-600" /><p className="overline !text-teal-600">Products & Services</p></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {m.products.map((p, i) => (
                  <article key={i} className="rounded-xl overflow-hidden border border-aubergine/8 group hover:shadow-luxe transition-all">
                    {p.image && (
                      <div className="aspect-[16/10] overflow-hidden bg-lavender">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                    )}
                    <div className="p-5">
                      <h4 className="font-display text-lg text-aubergine">{p.name}</h4>
                      {p.price && <p className="text-xs uppercase tracking-widest text-rose mt-1">{p.price}</p>}
                      {p.description && <p className="text-sm text-plumSoft font-light mt-3 leading-relaxed">{p.description}</p>}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 mx-6 md:mx-10 mb-10 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-aubergine to-plum text-ivory text-center relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-20" />
            <div className="relative">
              <p className="overline !text-champagne">Women Of Wisdom, Success, Happiness & Empowerment</p>
              <h2 className="font-display text-3xl md:text-4xl mt-3">Join {m.name?.split(" ")[0]} inside WOWSHE.</h2>
              <p className="mt-3 text-ivory/75 font-light max-w-xl mx-auto">An invitation-grade community of women founders, leaders and professionals. Workshops, mentorship, referrals and chapter dinners — by women, for women.</p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link to="/membership" data-testid="pub-join-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-champagne text-aubergine text-xs uppercase tracking-widest font-semibold hover:-translate-y-0.5 transition-all">
                  Become a WOWSHE Member <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/about" className="text-xs uppercase tracking-widest text-ivory/80 hover:text-champagne link-underline">Learn more</Link>
              </div>
            </div>
          </div>
        </article>

        <p className="text-center text-xs text-plumSoft mt-8">
          This is a public WOWSHE member profile · © {new Date().getFullYear()} WOWSHE · <Link to="/" className="link-underline">wowshe.in</Link>
        </p>
      </div>
    </div>
  );
}
